/**
 * Central tool registry
 * Manages registration, retrieval, and listing of all available tools
 */

import type { Tool } from '../types';
import { DEFAULT_TOOL_SEARCH_CONFIG, type ToolSearchConfig } from '../tool-search';
import { createArtifactToolDefinition } from '../../artifacts/create-artifact-tool';
import { logger } from '../../logger';

class ToolRegistry {
  private tools: Map<string, Tool> = new Map();
  private toolSearchConfig: ToolSearchConfig = DEFAULT_TOOL_SEARCH_CONFIG;

  /**
   * Register a tool
   */
  register(tool: Tool): void {
    if (this.tools.has(tool.id)) {
      logger.warn('tool is already registered, overwriting', { op: 'ToolRegistry.register', toolId: tool.id });
    }
    this.tools.set(tool.id, tool);
  }

  /**
   * Get a tool by ID
   */
  get(id: string): Tool | undefined {
    return this.tools.get(id);
  }

  /**
   * Get all tools
   */
  list(): Tool[] {
    return Array.from(this.tools.values());
  }

  /**
   * Get all tools (alias for list)
   */
  getAll(): Tool[] {
    return this.list();
  }

  /**
   * Get tools by category
   */
  getByCategory(category: 'query' | 'edit' | 'search'): Tool[] {
    return this.list().filter(tool => tool.category === category);
  }

  /**
   * Get non-deferred tools (loaded immediately)
   */
  getNonDeferredTools(): Tool[] {
    return this.list().filter(tool => {
      const toolId = tool.id;
      return this.toolSearchConfig.nonDeferredTools.includes(toolId);
    });
  }

  /**
   * Get deferred tools (loaded on-demand)
   */
  getDeferredTools(): Tool[] {
    return this.list().filter(tool => {
      const toolId = tool.id;
      return this.toolSearchConfig.deferredTools.includes(toolId);
    });
  }

  /**
   * Get tool search configuration
   */
  getToolSearchConfig(): ToolSearchConfig {
    return this.toolSearchConfig;
  }

  /**
   * Set tool search configuration
   */
  setToolSearchConfig(config: ToolSearchConfig): void {
    this.toolSearchConfig = config;
  }

  /**
   * Get tool schemas for Bedrock
   */
  getSchemas(): Array<{
    name: string;
    description: string;
    inputSchema: any;
  }> {
    return this.list().map(tool => ({
      name: tool.id,
      description: tool.description,
      inputSchema: tool.schema,
    }));
  }

  /**
   * Check if tool exists
   */
  has(id: string): boolean {
    return this.tools.has(id);
  }

  /**
   * Clear all tools (for testing)
   */
  clear(): void {
    this.tools.clear();
  }
}

// Singleton instance
export const toolRegistry = new ToolRegistry();

/**
 * Initialize registry with all tool definitions
 *
 * Always also registers the package's own built-in tools (currently
 * `create_artifact`) so every consumer that initializes the registry gets
 * them without enumerating package internals. A consumer-supplied definition
 * with the same id wins (registered after, overwriting the built-in).
 */
export function initializeToolRegistry(definitions: Record<string, Tool>): void {
  toolRegistry.register(createArtifactToolDefinition);
  Object.values(definitions).forEach(tool => {
    toolRegistry.register(tool);
  });
}
